import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSite } from '../hooks/useSites';
import { useToast } from '../hooks/useToast';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useEditorPreview } from '../hooks/useEditorPreview';
import { getSchema } from '../api/schemas';
import { getContentId } from '../types';
import { firstMissingRequiredField } from '../utils/validateRequired';
import FormField from '../components/FormField';
import '../styles/editor.css';

const BRANDING_FIELDS = ['name', 'logo', 'favicon', 'tagline', 'sectionLabel'];

export default function BrandingEditor() {
  const { siteId } = useParams<{ siteId: string }>();
  const { site } = useSite(siteId);
  const { items, loading, create, update } = useContent(siteId, 'site');
  const { addToast } = useToast();
  const { sendUpdate } = useEditorPreview('/');

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [originalData, setOriginalData] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);

  const schema = site ? getSchema(site.templateId) : undefined;
  const rawFields = schema?.contentTypes.site?.fields || {};
  const fields = Object.fromEntries(
    Object.entries(rawFields).filter(([name]) => BRANDING_FIELDS.includes(name))
  );

  const existingItem = items[0];
  const isDirty = JSON.stringify(formData) !== JSON.stringify(originalData);
  useUnsavedChanges(isDirty);

  useEffect(() => {
    if (existingItem) {
      const data = existingItem.data || {};
      setFormData(data);
      setOriginalData(data);
    }
  }, [existingItem]);

  const handleChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    sendUpdate('site', name, value);
  };

  const handleSave = async () => {
    if (!siteId) return;
    const missing = firstMissingRequiredField(fields, formData);
    if (missing) {
      addToast('error', `Please fill in "${missing}" before saving`);
      return;
    }
    setSaving(true);
    try {
      if (existingItem) {
        await update(getContentId(existingItem.contentKey), formData);
      } else {
        await create(formData);
      }
      setOriginalData({ ...formData });
      addToast('success', 'Branding saved');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Navigation & Branding</h1>
        <div className="page-header-actions">
          {isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="editor-form">
        {Object.entries(fields).map(([name, fieldSchema]) => (
          <FormField
            key={name}
            name={name}
            schema={fieldSchema}
            value={formData[name]}
            onChange={handleChange}
            siteId={siteId!}
            cmsType="site"
          />
        ))}
      </div>
    </div>
  );
}
